import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { CalendarDays, ClipboardList, ClipboardCheck, Settings as SettingsIcon, LogOut, Stethoscope, MapPin } from "lucide-react";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { visibleProposedTimes } from "../components/visibleProposedTimes";
import { fmtRange } from "../components/proposalRange";
import { tintMapFor, tintOf } from "../constants/locationTints";
import { locationLabelMap } from "../components/locationColors";
import { useLocationFilter, matchesLocationFilter } from "../lib/clinicLocationFilter";

// ── Shell layout for the Clinic Portal (Phase 1C) ─────────────────────────────
// Sibling to /portal (CLIENT) and the internal portal (admin/sales) — completely
// separate sidebar + content area scoped to a single network-clinic user.
// The pending-requests count badge is live-fetched on mount and re-fetched
// whenever child pages call window.dispatchEvent('clinic-pending-refresh').
export default function ClinicPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  // Spotlight nav (2026-06): the patient toggle list derives from THIS SAME
  // existing pending fetch — no new endpoint. Entries broadcast to the
  // scheduling page, which owns the single spotlight mechanism.
  const [pendingRequests, setPendingRequests] = useState([]);
  const [activeSpotlightId, setActiveSpotlightId] = useState(null);
  const [followUpCount, setFollowUpCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.get("/clinic/me")
      .then((r) => { if (!cancelled) setProfile(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const refreshPending = React.useCallback(() => {
    api.get("/scheduling/clinic/pending")
      .then((r) => {
        const reqs = (r.data?.requests || []).slice().sort(
          (a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))
        ); // newest first
        setPendingRequests(reqs);
        // one source of truth for nav entries AND spotlight advance order
        window.dispatchEvent(new CustomEvent("clinic-pending-list", { detail: { requests: reqs } }));
      })
      .catch(() => { setPendingRequests([]); });
  }, []);

  // D.2 — the badge and the list read the SAME shared session filter the
  // calendar, the legend chips and the Pending table read. Display only:
  // the fetch above is unchanged and sends no location parameter.
  const { selected: locFilter } = useLocationFilter();
  const visiblePending = useMemo(
    () => pendingRequests.filter((r) => matchesLocationFilter(locFilter, r.location_code)),
    [pendingRequests, locFilter]
  );
  const pendingCount = visiblePending.length;
  const isMultiLocation = (profile?.locations?.length || 0) > 1;
  const locLabels = useMemo(() => locationLabelMap(profile?.locations), [profile]);
  const tintMap = useMemo(() => tintMapFor(profile), [profile]);

  // Active-entry marking follows the page's spotlight state
  useEffect(() => {
    const handler = (e) => setActiveSpotlightId(e.detail?.id ?? null);
    window.addEventListener("clinic-spotlight-state", handler);
    return () => window.removeEventListener("clinic-spotlight-state", handler);
  }, []);

  // "M. Torres" style short name
  const shortName = (name, pid) => {
    if (!name) return `Patient #${pid}`;
    const parts = name.trim().split(/\s+/);
    return parts.length > 1 ? `${parts[0][0]}. ${parts.slice(1).join(" ")}` : name;
  };

  // Toggle = spotlight: same mechanism the card click drives (the scheduling
  // page's activateSpotlight) — event when mounted, sessionStorage handoff
  // when navigating from another clinic page.
  const handleNavEntry = (r) => {
    sessionStorage.setItem("clinic-spotlight-request", JSON.stringify(r));
    window.dispatchEvent(new CustomEvent("clinic-spotlight-request", { detail: { request: r } }));
    navigate("/clinic/scheduling");
  };

  const refreshFollowUps = React.useCallback(() => {
    api.get("/clinic/follow-ups")
      .then((r) => setFollowUpCount(r.data?.follow_ups?.length || 0))
      .catch(() => setFollowUpCount(0));
  }, []);

  useEffect(() => {
    refreshPending();
    refreshFollowUps();
    const pendingHandler = () => refreshPending();
    const followUpHandler = () => refreshFollowUps();
    window.addEventListener("clinic-pending-refresh", pendingHandler);
    window.addEventListener("clinic-follow-ups-refresh", followUpHandler);
    const t = setInterval(() => {
      refreshPending();
      refreshFollowUps();
    }, 30000); // 30s gentle poll for the sidebar counts
    return () => {
      window.removeEventListener("clinic-pending-refresh", pendingHandler);
      window.removeEventListener("clinic-follow-ups-refresh", followUpHandler);
      clearInterval(t);
    };
  }, [refreshPending, refreshFollowUps]);

  const navItem = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
      isActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-50"
    }`;

  return (
    <div className="flex h-screen bg-slate-50" data-testid="clinic-portal-shell">
      {/* Sidebar */}
      <aside className="w-72 bg-white border-r border-slate-200 flex flex-col flex-shrink-0">
        <div className="px-5 py-5 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
              <Stethoscope size={16} />
            </div>
            <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Clinic Portal</div>
          </div>
          <div data-testid="clinic-name" className="text-sm font-semibold text-slate-900 leading-snug">
            {profile?.location_name || "Loading…"}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
            <MapPin size={11} />
            {profile?.locations?.length > 1
              ? `${profile.locations.length} locations`
              : profile?.location_code || ""}
          </div>
          {user?.username && (
            <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
              Signed in as <span className="font-medium text-slate-700">{user.username}</span>
            </div>
          )}
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          <NavLink to="/clinic/scheduling" className={navItem} data-testid="clinic-nav-scheduling">
            <CalendarDays size={16} /> Scheduling Calendar
          </NavLink>
          <NavLink to="/clinic/pending" className={navItem} data-testid="clinic-nav-pending">
            <ClipboardList size={16} />
            <span className="flex-1">Pending Requests</span>
            {pendingCount > 0 && (
              <span
                data-testid="clinic-pending-badge"
                className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold"
              >
                {pendingCount}
              </span>
            )}
          </NavLink>
          {visiblePending.length > 0 && (
            <div
              className="ml-6 mr-1 max-h-64 overflow-y-auto space-y-0.5"
              data-testid="clinic-nav-pending-list"
            >
              {visiblePending.map((r) => {
                const active = r.id === activeSpotlightId;
                const times = visibleProposedTimes(r);
                const windows = times.length;
                const t = tintOf(tintMap, r.location_code);
                // D.4/D.5 — the first window as a RANGE, then how many
                // more there are; the sidebar never implies one time.
                const first = times[0] || null;
                return (
                  <button
                    key={r.id}
                    type="button"
                    data-testid={`clinic-nav-pending-${r.id}`}
                    onClick={() => handleNavEntry(r)}
                    className={`w-full text-left px-2 py-1.5 rounded-md text-xs transition ${
                      active
                        ? "bg-blue-100 text-blue-800 font-semibold ring-1 ring-blue-300"
                        : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                    }`}
                  >
                    <span className="flex items-center gap-1.5 truncate">
                      {isMultiLocation && (
                        <span
                          data-testid={`clinic-nav-pending-dot-${r.id}`}
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: t?.border }}
                        />
                      )}
                      <span className="truncate">
                        {shortName(r.patient_name, r.patient_id)} · {windows} window{windows === 1 ? "" : "s"}
                      </span>
                    </span>
                    {first && (
                      <span
                        data-testid={`clinic-nav-pending-range-${r.id}`}
                        className="block pl-0.5 text-[10px] opacity-80 truncate"
                      >
                        {fmtRange(first)}
                        {windows > 1 && ` · +${windows - 1} more`}
                      </span>
                    )}
                    {isMultiLocation && (
                      <span
                        data-testid={`clinic-nav-pending-loc-${r.id}`}
                        className="block pl-0.5 text-[10px] opacity-70 truncate"
                      >
                        {locLabels[r.location_code] || r.location_code}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <NavLink to="/clinic/follow-ups" className={navItem} data-testid="clinic-nav-follow-ups">
            <ClipboardCheck size={16} />
            <span className="flex-1">Follow-ups</span>
            {followUpCount > 0 && (
              <span
                data-testid="clinic-follow-ups-badge"
                className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold"
              >
                {followUpCount}
              </span>
            )}
          </NavLink>
          <NavLink to="/clinic/settings" className={navItem} data-testid="clinic-nav-settings">
            <SettingsIcon size={16} /> Settings
          </NavLink>
        </nav>

        <div className="px-3 py-4 border-t border-slate-100">
          <button
            type="button"
            data-testid="clinic-signout-btn"
            onClick={() => { logout(); navigate("/login"); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50 transition"
          >
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        <Outlet context={{ profile, refreshPending, refreshFollowUps }} />
      </main>
    </div>
  );
}

